import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Button } from 'antd';
import { useStableCallback } from '../../hooks/hookUtils';

const { TextArea } = Input;

interface ScratchpadModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialNotes: string;
  onUpdateNotes: (notes: string) => void;
}

const ScratchpadModal: React.FC<ScratchpadModalProps> = ({
  isOpen,
  onClose,
  initialNotes,
  onUpdateNotes,
}) => {
  const [notes, setNotes] = useState(initialNotes);
  // Track the previous initialNotes to detect external changes
  const prevInitialNotesRef = useRef(initialNotes);

  // When the modal opens or initialNotes changes externally, sync the content.
  // Using a ref comparison avoids the effect running on every render while still
  // catching genuine external updates to initialNotes.
  useEffect(() => {
    if (isOpen && initialNotes !== prevInitialNotesRef.current) {
      setNotes(initialNotes);
      prevInitialNotesRef.current = initialNotes;
    } else if (isOpen && notes === '') {
      // Handle initial open when notes might be empty
      setNotes(initialNotes);
      prevInitialNotesRef.current = initialNotes;
    }
  }, [isOpen, initialNotes, notes]);

  // Stable callback for updating the scratchpad notes, ensures the callback reference
  // doesn't cause the debounce effect to reset unnecessarily
  const stableUpdateNotes = useStableCallback((newNotes: string) => {
    onUpdateNotes(newNotes);
  });

  // Debounce the update to the global state.
  // By using stableUpdateNotes (a stable callback), we avoid including the
  // onUpdateNotes prop in deps, which would cause the timeout to reset on every render.
  useEffect(() => {
    // Only set up debounce if modal is open and notes differ from initial
    if (!isOpen || notes === initialNotes) {
      return;
    }

    const handler = setTimeout(() => {
      stableUpdateNotes(notes);
    }, 500);

    return () => clearTimeout(handler);
  }, [notes, isOpen, initialNotes, stableUpdateNotes]);

  return (
    <Modal
      title="Scratchpad"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <TextArea
        rows={15}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Quick notes, dice rolls, etc."
      />
    </Modal>
  );
};

export default ScratchpadModal;